import React, { useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaArrowRight, FaCheckCircle, FaCalendarAlt, FaUsers, FaGlobe, FaAward } from "react-icons/fa";
import about from "../../../assest/images/about.png";

/* ------------------------------------------------------------------ */
/*  About section — completely redesigned                              */
/* ------------------------------------------------------------------ */
const ABOUT_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,600;1,9..144,500&family=Manrope:wght@400;500;600;700;800&display=swap');

  .abt-display { font-family: 'Fraunces', ui-serif, Georgia, serif; }
  .abt-body { font-family: 'Manrope', ui-sans-serif, system-ui, sans-serif; }

  @keyframes abt-float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
  }
  .abt-float { animation: abt-float 4s ease-in-out infinite; }

  @media (prefers-reduced-motion: reduce) {
    .abt-float { animation: none !important; }
  }
`;

const milestones = [
  { icon: <FaCalendarAlt />, label: "Founded", value: "2019", color: "#D97706" },
  { icon: <FaUsers />, label: "Premium Partners", value: "500+", color: "#EA580C" },
  { icon: <FaGlobe />, label: "Countries", value: "40+", color: "#B45309" },
  { icon: <FaAward />, label: "Impressions", value: "1B+", color: "#D97706" },
];

const checklist = [
  "360° Ad Monetization",
  "State-of-the-Art Platform",
  "Dedicated Specialist Team",
  "Top-Tier Client Success",
];

function About() {
  const imgRef = useRef(null);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.12 });
  const prefersReducedMotion = useReducedMotion();

  const handleMouseMove = (e) => {
    if (prefersReducedMotion) return;
    const el = imgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / 30;
    const y = (e.clientY - rect.top - rect.height / 2) / 30;
    el.style.transform = `translate(${x}px, ${y}px)`;
  };

  const resetTransform = () => {
    const el = imgRef.current;
    if (el) el.style.transform = "translate(0px, 0px)";
  };

  return (
    <section
      id="aboutID"
      ref={ref}
      className="abt-body relative py-20 md:py-32 overflow-hidden border-t border-[#F5DFC5]"
      style={{
        background:
          "radial-gradient(90% 70% at 85% 0%, #FFF4E5 0%, transparent 55%), radial-gradient(80% 60% at 5% 100%, #FEF3E5 0%, transparent 55%), #FFFBF5",
      }}
    >
      <style>{ABOUT_STYLES}</style>

      {/* Ambient glow */}
      <div
        aria-hidden="true"
        className="absolute bottom-0 right-[-10%] w-[500px] h-[400px] opacity-[0.12] blur-[100px] pointer-events-none"
        style={{ background: "radial-gradient(circle, #D97706, transparent 70%)" }}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl relative z-10">
        {/* ---- SECTION HEADER ---- */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20 max-w-2xl mx-auto"
        >
          <div className="flex items-center w-full gap-4 mb-5">
            <span
              className="hidden sm:inline-block h-[2px] flex-1"
              style={{
                background: "linear-gradient(to left, #EA580C, transparent)",
              }}
            />
            <span className="text-xs sm:text-sm font-bold tracking-wide text-[#92400E] whitespace-nowrap">
              Company Journey &amp; Vision
            </span>
            <span
              className="hidden sm:inline-block h-[2px] flex-1"
              style={{
                background: "linear-gradient(to right, #EA580C, transparent)",
              }}
            />
          </div>

          <h2 className="abt-display text-3xl sm:text-4xl lg:text-[3.2rem] text-[#1C1410] leading-[1.12]" style={{ fontWeight: 600 }}>
            About{" "}
            <span className="relative inline-block whitespace-nowrap">
              <span
                className="bg-clip-text text-transparent bg-gradient-to-r from-[#D97706] via-[#E8882D] to-[#EA580C]"
                style={{ fontStyle: "italic", fontWeight: 500 }}
              >
                Us
              </span>
              <svg className="absolute left-0 -bottom-1.5 w-full" height="9" viewBox="0 0 120 9" preserveAspectRatio="none" aria-hidden="true">
                <path d="M2,6 C24,1 46,8 62,4 C78,0 98,7 118,3" fill="none" stroke="#D97706" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </span>
          </h2>

          <p className="mt-4 text-[#4D3E30] text-sm sm:text-base leading-relaxed">
            Founded in 2019, we've built a strong reputation in the digital
            market through dedication and exceptional service.
          </p>
        </motion.div>

        {/* ---- MAIN CONTENT GRID ---- */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* ---------- LEFT: Image with floating milestone cards ---------- */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <div
              className="relative max-w-sm mx-auto"
              onMouseMove={handleMouseMove}
              onMouseLeave={resetTransform}
            >
              {/* Main image */}
              <div
                ref={imgRef}
                className="relative w-full aspect-[4/5] overflow-hidden shadow-[0_30px_80px_-25px_rgba(28,20,16,0.25)] border-[3px] border-white transition-transform duration-200 ease-out"
                style={{ borderRadius: "2.5rem 1rem 2.5rem 1rem" }}
              >
                <img
                  src={about}
                  alt="MyAd360 team and office"
                  className="w-full h-full object-cover"
                />
                {/* Warm overlay */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(180deg, transparent 50%, rgba(28,20,16,0.45) 100%)" }}
                />

                {/* "Est. 2019" badge pinned inside the image */}
                <div className="absolute bottom-5 left-5 bg-white/90 backdrop-blur px-4 py-2 shadow-lg" style={{ borderRadius: "1rem 0.4rem 1rem 0.4rem" }}>
                  <p className="text-[10px] font-bold text-[#8A7560] uppercase">Established</p>
                  <p className="text-lg font-extrabold text-[#1C1410]" style={{ fontFamily: "'Fraunces', serif" }}>2019</p>
                </div>
              </div>

              {/* Floating milestone card — top right */}
              <motion.div
                initial={{ opacity: 0, y: -14 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="abt-float absolute -top-4 -right-4 sm:-right-8 z-20 bg-[#1C1410] text-white px-4 py-3 shadow-2xl"
                style={{ borderRadius: "0.5rem 1.4rem 0.5rem 1.4rem" }}
              >
                <p className="text-[10px] text-[#A08E7A] font-semibold">Premium Partners</p>
                <p className="text-lg font-extrabold text-[#F59E0B]">500+</p>
              </motion.div>

              {/* Floating milestone card — bottom right */}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.75 }}
                className="absolute -bottom-4 -right-3 sm:-right-6 z-20 bg-white px-4 py-3 shadow-xl border border-[#FDE8CD]"
                style={{ borderRadius: "1.4rem 0.5rem 1.4rem 0.5rem", animationDelay: "-2s" }}
              >
                <p className="text-[10px] text-[#8A7560] font-semibold">Global Reach</p>
                <p className="text-lg font-extrabold text-[#D97706]">40+ Countries</p>
              </motion.div>
            </div>
          </motion.div>

          {/* ---------- RIGHT: Story + Checklist + CTA ---------- */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-7 space-y-7"
          >
            {/* Blockquote */}
            <div
              className="relative bg-white border border-[#F0DCC8] p-6 sm:p-8 shadow-sm"
              style={{ borderRadius: "1rem 2rem 1rem 2rem" }}
            >
              <div
                aria-hidden="true"
                className="absolute top-0 left-6 w-1 h-full rounded-full"
                style={{ background: "linear-gradient(180deg, #D97706, #EA580C)" }}
              />
              <div className="pl-5">
                <p
                  className="abt-display text-lg sm:text-xl text-[#1C1410] leading-snug mb-4"
                  style={{ fontStyle: "italic", fontWeight: 500 }}
                >
                  "We began our journey in 2019, and in a short span of time, we've built a strong reputation in the digital market."
                </p>
                <p className="text-[#4D3E30] text-sm sm:text-base leading-relaxed">
                  This success is a testament to the dedication of our highly
                  skilled team and the exceptional services we provide.
                  Specializing in 360-degree ad monetization and optimization
                  solutions, we empower our clients with the tools they need
                  to maximize their revenue. Our state-of-the-art platform is
                  designed for ease of use, allowing clients to manage their
                  ads effortlessly.
                </p>
              </div>
            </div>

            {/* Checklist */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {checklist.map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: -12 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.35 + i * 0.1 }}
                  className="group flex items-center gap-3 bg-gradient-to-r from-[#FFFBF5] to-[#FEF3E5] border border-[#F0DCC8] px-4 py-3.5 hover:border-[#D97706]/50 hover:shadow-sm transition-all duration-300"
                  style={{
                    borderRadius: i % 2 === 0 ? "1.2rem 0.4rem 1.2rem 0.4rem" : "0.4rem 1.2rem 0.4rem 1.2rem",
                  }}
                >
                  <FaCheckCircle className="text-[#D97706] text-base flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="text-xs sm:text-sm font-semibold text-[#3D2E22]">{item}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <a
                href="#contactID"
                className="group relative overflow-hidden inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-[#D97706] to-[#B45309] hover:from-[#7C3A0E] hover:to-[#D97706] text-white font-bold px-7 py-3.5 shadow-[0_16px_34px_-14px_rgba(217,119,6,0.5)] transition-all duration-300 text-sm sm:text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D97706]"
                style={{ borderRadius: "1.8rem 0.6rem 1.8rem 0.6rem" }}
              >
                <span className="relative z-10 flex items-center gap-2.5">
                  Learn More
                  <FaArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
                <span
                  aria-hidden="true"
                  className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12"
                />
              </a>
            </motion.div>
          </motion.div>
        </div>

        {/* ---- MILESTONE COUNTER BAR ---- */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 md:mt-20"
        >
          <div
            className="bg-[#1C1410] p-6 sm:p-8 grid grid-cols-2 lg:grid-cols-4 gap-6 relative overflow-hidden"
            style={{ borderRadius: "2rem 0.75rem 2rem 0.75rem" }}
          >
            {/* Ambient glow */}
            <div
              aria-hidden="true"
              className="absolute -top-10 left-1/2 -translate-x-1/2 w-[500px] h-[200px] opacity-15 blur-[80px] pointer-events-none"
              style={{ background: "radial-gradient(circle, #D97706, transparent 70%)" }}
            />

            {milestones.map((m, i) => (
              <motion.div
                key={m.label}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.6 + i * 0.1 }}
                className="relative z-10 text-center group"
              >
                <div
                  className="w-12 h-12 mx-auto mb-3 flex items-center justify-center text-lg transition-transform duration-300 group-hover:scale-110"
                  style={{
                    background: `linear-gradient(145deg, ${m.color}22, ${m.color}11)`,
                    color: m.color,
                    borderRadius: "50% 50% 60% 40% / 40% 60% 40% 60%",
                  }}
                >
                  {m.icon}
                </div>
                <p className="text-2xl sm:text-3xl font-extrabold text-white mb-1" style={{ fontFamily: "'Fraunces', serif" }}>
                  {m.value}
                </p>
                <p className="text-[11px] sm:text-xs font-semibold text-[#A08E7A] uppercase tracking-wider">
                  {m.label}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default About;